import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Box,
  Button,
  Container,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import EventIcon from '@mui/icons-material/Event'
import WorkIcon from '@mui/icons-material/Work'
import PersonIcon from '@mui/icons-material/Person'

import { getCancelBookingInfo, confirmCancelBooking } from '../services/api'

function CancelBooking() {
  const { token } = useParams()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [jobInfo, setJobInfo] = useState(null)
  const [error, setError] = useState(null)
  const [cancelled, setCancelled] = useState(false)

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const data = await getCancelBookingInfo(token)
        setJobInfo(data)
      } catch (err) {
        const responseData = err.response?.data
        if (responseData?.status === 'expired') {
          setError({
            title: 'Link expired',
            message:
              responseData.message ||
              'This cancellation link has expired. Please contact the business directly.',
          })
        } else if (responseData?.status === 'already_cancelled') {
          setError({
            title: 'Already cancelled',
            message:
              responseData.message ||
              'This booking has already been cancelled.',
          })
        } else {
          setError({
            title: 'Invalid link',
            message:
              responseData?.message ||
              'This cancellation link is invalid. Please check the link and try again.',
          })
        }
      } finally {
        setLoading(false)
      }
    }
    fetchInfo()
  }, [token])

  const handleCancel = async () => {
    setSubmitting(true)
    try {
      await confirmCancelBooking(token)
      setCancelled(true)
    } catch (err) {
      const responseData = err.response?.data
      setError({
        title: 'Cancellation failed',
        message:
          responseData?.message ||
          'Something went wrong. Please try again or contact the business directly.',
      })
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ErrorOutlineIcon sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
          <Typography variant='h5' gutterBottom>
            {error.title}
          </Typography>
          <Typography color='text.secondary'>{error.message}</Typography>
        </Paper>
      </Container>
    )
  }

  if (cancelled) {
    return (
      <Container maxWidth='sm' sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
          />
          <Typography variant='h5' gutterBottom>
            Booking cancelled
          </Typography>
          <Typography color='text.secondary'>
            Your booking has been successfully cancelled. You will receive a
            confirmation email shortly.
          </Typography>
        </Paper>
      </Container>
    )
  }

  const { job } = jobInfo || {}
  const iconSx = { color: 'text.secondary', fontSize: 20 }

  return (
    <Container maxWidth='sm' sx={{ py: 8 }}>
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <WarningAmberIcon
          sx={{ fontSize: 64, color: 'warning.main', mb: 2 }}
        />
        <Typography variant='h5' gutterBottom>
          Cancel your booking?
        </Typography>
        <Typography color='text.secondary' sx={{ mb: 3 }}>
          Are you sure you want to cancel this booking? This action cannot be
          undone.
        </Typography>

        {job && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ textAlign: 'left', px: 2, mb: 3 }}>
              {job.bookingDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <EventIcon sx={iconSx} />
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Date
                    </Typography>
                    <Typography variant='body2'>
                      {new Date(job.bookingDate).toLocaleDateString(undefined, {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Box>
                </Box>
              )}
              {job.jobType && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <WorkIcon sx={iconSx} />
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Service
                    </Typography>
                    <Typography variant='body2'>{job.jobType}</Typography>
                  </Box>
                </Box>
              )}
              {job.customerName && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1 }}>
                  <PersonIcon sx={iconSx} />
                  <Box>
                    <Typography variant='caption' color='text.secondary'>
                      Customer
                    </Typography>
                    <Typography variant='body2'>{job.customerName}</Typography>
                  </Box>
                </Box>
              )}
            </Box>
            <Divider sx={{ mb: 3 }} />
          </>
        )}

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant='contained'
            color='error'
            size='large'
            onClick={handleCancel}
            disabled={submitting}
            sx={{ minWidth: 180 }}
          >
            {submitting ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Yes, cancel booking'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  )
}

export default CancelBooking
